/* eslint-disable no-underscore-dangle */
import React, {
  lazy, Suspense, useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import FollowUpService from '../../../Services/FollowUpService';
import MessagePresetsService from '../../../Services/MessagePresetsService';
import { firstName } from '../../Portal/portalFormat';
import {
  flattenLines, hasLineBreak, oneLine, pasteOneLine,
} from '../../../Utils/oneLine';
import {
  looksUnresolved, templateBlocks, TEMPLATE_VARIABLE_MAX,
} from '../../../Utils/passengerTemplate';
import {
  defaultEmailSubject, isEmailChannel, presetsForChannel,
} from '../../../Utils/messageChannel';

/**
 * Message the passenger of one booking — the office's single composer, on
 * either channel.
 *
 * THIS WAS `FollowUpModal`, and it was abandoned-basket-only: it existed
 * behind one trigger on `status === 'Request'`, drafted with AI the instant
 * it opened, and was unreachable for every other booking. But the thing the
 * office actually does most is type one operational line — payment is
 * required, your driver is outside, nobody came to the kerb — and that is
 * the same message, the same endpoint and the same audit trail as a basket
 * nudge. So there is ONE composer, and the basket's trigger is now one
 * caller of it rather than its owner.
 *
 * WHAT THAT CHANGED. AI drafting became a BUTTON instead of what happens on
 * open: somebody who wants to write "Your driver is at exit B" should not
 * wait on a model, and the old version blocked the form behind a spinner
 * before they could type a character. Nothing else about the send moved —
 * `FollowUpService.send` → `followup-send` → `MessageDispatchService` was
 * already shared, and no new endpoint was added for any of this.
 *
 * …AND THEN ONE DOOR GOT ITS AUTOMATIC DRAFT BACK (`autoDraft`). The
 * abandoned basket is not the same job as the rest: there is no operational
 * line to type, the whole message IS the persuasion, and nobody opens that
 * panel for any other reason. So that caller — and only that caller — asks
 * for a draft on open. The generic doors (the Orders table's phone number,
 * the passenger-information row) stay manual, because somebody who tapped a
 * phone number already knows what they want to say.
 *
 * It is a PER-ENTRY-POINT BEHAVIOUR, not a second composer, and the old
 * mistake is not repeated: an automatic draft does NOT block the form. The
 * field stays enabled and empty, the sender can start typing immediately,
 * and a draft that lands while they are typing is DISCARDED rather than
 * written over the top of them. A draft that fails leaves an error and a
 * usable composer, never a dead modal.
 *
 * THE WHATSAPP BODY IS ONE LINE, and that is the whole of the design.
 * The passenger receives the approved `we_have_a_message_for_you` template:
 * a greeting with their first name, a fixed line, then the body. Two
 * consequences the old textarea got wrong.
 *
 * First, the sender could not SEE the greeting, so the natural thing was to
 * type "Hi Anna" into the body and have Anna greeted twice. The preview
 * shows all three blocks as they will be read, with the two fixed ones
 * styled as not-yours.
 *
 * Second, `rows={8}` invited paragraphs. The server flattens a template
 * variable (`toTemplateVariable`), so a newline does not fail the send — it
 * arrives as one run-on line the passenger has to untangle. The field is
 * therefore a single-line input: Enter sends, and a pasted paragraph is
 * flattened to SPACES as it arrives rather than refused. Nobody is punished
 * for a paste. (The paste is intercepted on purpose — left alone, a
 * single-line input strips newlines rather than replacing them and welds the
 * words together; `pasteOneLine` holds the detail.) The EMAIL branch keeps
 * its rich editor, which has no such constraint.
 *
 * THE EDITOR IS LAZY ON PURPOSE. `HtmlEditor` pulls in `react-quill-new`,
 * which ships ESM that CRA's Jest cannot transform — which is why every
 * spec that touched the old modal had to mock the whole thing away, and why
 * the WhatsApp path had no test at all. Behind `lazy()` the editor is out of
 * the static module graph, so the WhatsApp half can be rendered and asserted
 * on directly, and the email half still loads the editor the moment it is
 * actually shown.
 *
 * THE LENGTH CAP IS THE TEMPLATE'S, not this endpoint's. Meta rejects an
 * over-long template variable at its own edge, and the portal's
 * `POST /portal/message` refuses one early for that reason
 * (`TEMPLATE_VARIABLE_MAX`). Whether `followup-send` enforces the same number
 * has NOT been verified — so the count here is for consistency with the
 * portal composer and to keep the office inside what Meta will carry, and it
 * is not a claim about what this path would reject.
 *
 * THE SAVED MESSAGES ARE FETCHED FOR THIS BOOKING, not passed in. The
 * composer asks `/message-presets?booking_id=…` as it opens, and what comes
 * back has already had its variables filled in by the server: "from
 * {pickup} at {time}" arrives as "from Terminal 2A at 14:30". That is a
 * server job rather than a browser one for one specific reason — the free
 * waiting allowances a message like that quotes are the BOOKING'S PARTNER'S
 * commercial terms, and the browser has no way to know Booking.com's
 * minutes differ from ours. Resolving before the text reaches this field is
 * also what lets the sender read and edit the FINAL words instead of a row
 * of placeholders, and it leaves the wire contract exactly as it was.
 *
 * Picking one FILLS the field and leaves it editable, so a preset is a
 * starting point rather than something sent blind. Only `body` ever reaches
 * the wire — and, on email, the `subject` it carries. A label is for the
 * sender.
 *
 * AND THE SAVED MESSAGES ARE PER CHANNEL. A WhatsApp snippet is written for
 * a template variable — one line, capped, no greeting — and an email one
 * carries a subject and may run to paragraphs. They are not interchangeable,
 * so only the channel in use is offered (`Utils/messageChannel`). A snippet
 * with no `channel` is a WhatsApp one: the rows that predate the field were
 * all written for the template, and the API says the same from its side.
 *
 * A preset the booking cannot fill is NOT OFFERED (the server withholds it
 * and says why) — because a passenger must never read a literal "{pickup}".
 * The count of those is shown as a note rather than left silent: a chip
 * that simply is not there teaches nobody that a saved message exists.
 */

// Out of the static graph: see THE EDITOR IS LAZY ON PURPOSE above.
const HtmlEditor = lazy(() => import('../../ServiceOffers/Components/HtmlEditor'));

const PLACEHOLDER = 'Your message appears here';

/** How long the success Alert stays up before the modal bows out. */
const CLOSE_AFTER_MS = 1200;

/** The template as the passenger will read it: the two fixed blocks, then the body. */
function TemplatePreview({ first, body }) {
  const blocks = templateBlocks({ first, body, placeholder: PLACEHOLDER });

  return (
    <div className="border rounded-3 bg-warning-subtle px-3 py-2 mb-3">
      {blocks.map((block) => (
        <p
          key={block.id}
          className={[
            'mb-0 py-1',
            // The baked-in blocks read as part of the message but not as
            // something to type over; the body is the sender's own.
            block.fixed ? 'text-secondary fw-semibold' : 'fw-bold',
            block.empty ? 'fst-italic text-muted fw-normal' : '',
          ].filter(Boolean).join(' ')}
          data-testid={`passenger-message-${block.id}`}
        >
          {block.text}
        </p>
      ))}
    </div>
  );
}

function PassengerMessageModal({
  show, channel = 'whatsapp', booking, onHide, onSent, autoDraft = false,
}) {
  const isEmail = isEmailChannel(channel);

  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  // Named once so the reset, the prefill and a draft with no subject of its
  // own all land on the same string.
  const emailSubject = defaultEmailSubject(booking?.booking_reference);
  const [subject, setSubject] = useState(emailSubject);
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  // The saved messages, resolved against THIS booking. `omitted` is the
  // ones that cannot be sent against it, kept so the absence of a chip is
  // explainable rather than mysterious.
  const [presets, setPresets] = useState([]);
  const [omitted, setOmitted] = useState([]);

  const recipient = isEmail ? booking?.email_address : booking?.phone_number;
  const bookingId = booking?._id;
  // `{{1}}`. The server derives it again from the booking and its answer
  // wins; this is what the sender is shown while composing.
  const first = firstName(booking?.full_name);

  // The auto-close timer, cleared on unmount so a modal that is closed by
  // hand never fires a state update (or a log) after the fact.
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // What is in the fields right now, readable from inside an async draft
  // without making the draft depend on them. See `draftWithAi`.
  const written = useRef({ text: '', subject: '', html: '' });
  written.current = { text, subject, html };

  /**
   * Draft with the model.
   *
   * `onlyIfEmpty` is the difference between the two callers. The BUTTON
   * overwrites, because the sender just asked for a draft and watching it
   * refuse to appear would be baffling. The automatic draft on the
   * abandoned-basket door does NOT: the field is live from the first frame,
   * so somebody can be half-way through a sentence by the time the model
   * answers, and their words win.
   */
  const draftWithAi = async ({ onlyIfEmpty = false } = {}) => {
    if (!bookingId) return;
    setDrafting(true);
    setError('');
    try {
      const data = await FollowUpService.generate(bookingId, channel);
      const content = data?.content || {};
      if (isEmail) {
        // THE BODY, not the subject. The subject arrives prefilled from the
        // booking, so it is no longer evidence that somebody started
        // writing — testing it would make the abandoned-basket door's
        // automatic draft bail out every single time.
        if (onlyIfEmpty && written.current.html.trim()) return;
        // A draft with no subject of its own keeps the booking's, rather
        // than blanking a line the sender could already read.
        setSubject(content.subject || emailSubject);
        setHtml(content.html || '');
      } else {
        if (onlyIfEmpty && written.current.text.trim()) return;
        // A model writes paragraphs; this field holds one line.
        setText(flattenLines(content.text || ''));
      }
    } catch (err) {
      // An error and a USABLE composer. A failed draft must never be the end
      // of the sender's options — the field is still empty and still theirs.
      setError(err.response?.data?.message || 'Failed to draft the message. Please try again.');
    } finally {
      setDrafting(false);
    }
  };

  // A fresh composer each time it opens, on whichever channel — and, for the
  // abandoned-basket door only, a draft on the way. The fields are cleared
  // and left ENABLED either way: the old version's blocking spinner is the
  // thing this must not become again.
  useEffect(() => {
    if (!show) return;
    setSent(false);
    setError('');
    setSubject(emailSubject);
    setHtml('');
    setText('');
    written.current = { text: '', subject: emailSubject, html: '' };
    // Fire-and-forget: the draft lands in a composer that is already live,
    // and `draftWithAi` owns its own error. Deliberately not in the
    // dependency list — it closes over the channel and would re-run the
    // reset on every render.
    if (autoDraft) draftWithAi({ onlyIfEmpty: true });
  }, [show, channel, bookingId, autoDraft]);

  /**
   * The saved messages for THIS booking, with their variables already
   * filled in by the server. Fetched on open, not held by the caller: every
   * door onto this composer would otherwise need its own copy of the fetch,
   * and they would drift.
   *
   * A failure here is SILENT on purpose. Presets are a shortcut, not the
   * feature; an agent who came to type one line should not be shown an
   * error about a list they were not going to use.
   */
  useEffect(() => {
    if (!show || !bookingId) return undefined;

    let live = true;
    setPresets([]);
    setOmitted([]);

    MessagePresetsService.fetchPresetsForBooking(bookingId)
      .then((payload) => {
        if (!live) return;
        setPresets(payload.presets);
        setOmitted(payload.omitted);
      })
      .catch(() => {});

    return () => { live = false; };
  }, [show, bookingId]);

  // ONLY THIS CHANNEL'S saved messages. Filtered here rather than re-fetched
  // with `?channel=`: the payload already carries each row's channel, and one
  // read serves whichever channel this door opened on.
  const chips = presetsForChannel(presets, channel);
  const chipsOmitted = presetsForChannel(omitted, channel);

  // What the WhatsApp branch would actually put on the wire.
  const wire = oneLine(text);
  const over = wire.length - TEMPLATE_VARIABLE_MAX;
  // A variable nobody filled in. Advice, not a block: the server is the
  // authority and refuses the send itself (see Utils/passengerTemplate).
  const unresolved = isEmail ? looksUnresolved(html) : looksUnresolved(wire);
  const ready = isEmail
    ? Boolean(subject.trim() && html.trim())
    : Boolean(wire) && over <= 0;

  /**
   * Fill the composer from a saved message, leaving everything editable.
   *
   * An email snippet carries BOTH halves, so it fills both — but one with no
   * subject of its own leaves the booking's prefilled subject alone rather
   * than blanking a line the sender could already read.
   */
  const applyPreset = (preset) => {
    if (isEmail) {
      if (preset.subject) setSubject(preset.subject);
      setHtml(preset.body || '');
      return;
    }
    // A WhatsApp body is a template variable: one line, however it arrives.
    setText(flattenLines(preset.body));
  };

  const trySend = useCallback(async () => {
    // `drafting` is NOT a reason to refuse. Somebody who typed their own
    // line while a draft was in flight has said what they want to send, and
    // the draft will not overwrite it (`onlyIfEmpty`).
    if (sending || sent) return;
    setError('');

    if (!recipient) {
      setError(isEmail ? 'This booking has no email address.' : 'This booking has no phone number.');
      return;
    }
    if (!ready) {
      setError(isEmail
        ? 'Subject and message body are required.'
        : 'Write the message you want the passenger to receive.');
      return;
    }

    setSending(true);
    try {
      // The ONE line that goes out, flattened once here: `followup-send`
      // takes the body as given.
      const content = isEmail ? { subject, html } : { text: wire };
      const result = await FollowUpService.send(bookingId, channel, content);
      setSent(true);
      // Lets the caller record who/when, so the booking's own "Messaged by …"
      // list updates without waiting for a re-fetch.
      if (onSent && result?.followUp) onSent(result.followUp);
      closeTimer.current = setTimeout(() => onHide(), CLOSE_AFTER_MS);
    } catch (err) {
      // The typed text is LEFT ALONE — a failed send is the one moment
      // nobody should have to write their message again.
      setError(err.response?.data?.message || 'Failed to send the message. Please try again.');
    } finally {
      setSending(false);
    }
  }, [
    sending, sent, recipient, isEmail, ready, subject, html, wire,
    bookingId, channel, onSent, onHide,
  ]);

  const title = isEmail ? 'Email the Passenger' : 'Message the Passenger';
  // WhatsApp is a Font Awesome *brand* icon — it needs the `fab` prefix, not
  // the solid `fa`, or the glyph renders blank.
  const icon = isEmail ? 'fa fa-envelope' : 'fab fa-whatsapp';
  const busy = sending || drafting;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`${icon} me-2`} />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Form
        onSubmit={(event) => {
          event.preventDefault();
          trySend();
        }}
      >
        <Modal.Body>
          <div className="text-muted mb-3">
            To:
            {' '}
            <strong>{recipient || (isEmail ? 'no email on booking' : 'no phone on booking')}</strong>
          </div>

          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          {sent && (
            <Alert variant="success" className="py-2">
              <i className="fa fa-check me-2" />
              Message sent.
            </Alert>
          )}

          {!isEmail && <TemplatePreview first={first} body={text} />}

          {/* `controlId` so the label actually points at the field: this
              card can show a subject and a body at once, and an unlabelled
              box is the one a screen-reader user cannot tell apart. */}
          {isEmail && (
            <Form.Group className="mb-3" controlId="passenger-message-subject">
              <Form.Label className="small fw-semibold">Subject</Form.Label>
              <Form.Control
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                // `sending`, NOT `busy`: a draft in flight must never take
                // the keyboard away — see …AND THEN ONE DOOR above.
                disabled={sending}
              />
            </Form.Group>
          )}

          {/* The saved messages, already resolved against this booking.
              Picking one FILLS the field and leaves it editable. */}
          {chips.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-label="Saved messages">
              {chips.map((preset) => (
                <Button
                  key={preset.id || preset.label}
                  variant="outline-secondary"
                  size="sm"
                  disabled={sending || sent}
                  title={preset.body}
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Said rather than left silent: a chip that is simply absent
              teaches nobody that the saved message exists. The server's
              reason names the variable this booking cannot fill. */}
          {chipsOmitted.length > 0 && (
            <div className="small text-muted mb-3" data-testid="passenger-message-omitted">
              <i className="fa fa-circle-info me-1" />
              {chipsOmitted.length === 1
                ? '1 saved message is not available for this booking: '
                : `${chipsOmitted.length} saved messages are not available for this booking: `}
              {chipsOmitted.map((preset) => `${preset.label} — ${preset.reason}`).join(' ')}
            </div>
          )}

          <Form.Group className="mb-2">
            {/* The editor is a Quill surface with no form control to point
                at, so only the single-line field gets a `for`. */}
            {isEmail ? (
              <Form.Label className="small fw-semibold">Message</Form.Label>
            ) : (
              <Form.Label className="small fw-semibold" htmlFor="passenger-message-body">
                Message
              </Form.Label>
            )}
            {isEmail ? (
              <Suspense fallback={(
                <div className="text-center text-muted py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading the editor…
                </div>
              )}
              >
                <HtmlEditor value={html} onChange={setHtml} placeholder="Email body…" />
              </Suspense>
            ) : (
              <>
                <Form.Control
                  id="passenger-message-body"
                  type="text"
                  value={text}
                  autoComplete="off"
                  placeholder="Your driver is waiting at exit B"
                  // `sending`, NOT `busy`: an automatic draft arrives into a
                  // composer somebody can already type in, and a failed one
                  // leaves them able to write the message themselves.
                  disabled={sending}
                  // A single-line input cannot HOLD a line break, and a paste
                  // that arrives with some is flattened rather than refused.
                  onChange={(e) => setText(flattenLines(e.target.value))}
                  // A multi-line paste is intercepted rather than left to
                  // the browser, which would STRIP the newlines and weld the
                  // words together before onChange could see them. A paste
                  // with nothing to flatten keeps native behaviour.
                  onPaste={(e) => {
                    const pasted = e.clipboardData?.getData('text') || '';
                    if (!hasLineBreak(pasted)) return;
                    e.preventDefault();
                    setText(pasteOneLine({
                      value: text,
                      pasted,
                      start: e.target.selectionStart,
                      end: e.target.selectionEnd,
                    }));
                  }}
                  // Explicit rather than relying on implicit form submission,
                  // so Enter does the same thing in every browser.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      trySend();
                    }
                  }}
                />
                <Form.Text className="text-muted">
                  One line. Press Enter to send — a line break would reach the passenger
                  as one run-on line, so a pasted paragraph becomes spaces.
                  <span
                    className={over > 0 ? 'text-danger fw-bold ms-1' : 'ms-1'}
                    data-testid="passenger-message-count"
                  >
                    {`${wire.length}/${TEMPLATE_VARIABLE_MAX}`}
                  </span>
                </Form.Text>
                {over > 0 && (
                  <div className="text-danger small fw-bold mt-1" data-testid="passenger-message-too-long">
                    {`Too long to send by ${over} character${over === 1 ? '' : 's'}. `}
                    Shorten it and the send will arm itself.
                  </div>
                )}
                {unresolved && (
                  <div
                    className="text-warning-emphasis small fw-bold mt-1"
                    data-testid="passenger-message-unresolved"
                  >
                    {`${unresolved} will reach the passenger exactly as written. `}
                    Replace it with the real words — a saved message fills its own
                    variables in when you pick it.
                  </div>
                )}
              </>
            )}
          </Form.Group>

          {/* Drafting is OFFERED, never imposed: the common case is one
              typed line, and the old version made everybody wait on a model
              before they could type it. */}
          <Button
            variant="link"
            size="sm"
            className="px-0"
            onClick={() => draftWithAi()}
            disabled={busy || sent}
          >
            {drafting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Drafting…
              </>
            ) : (
              <>
                <i className="fa fa-wand-magic-sparkles me-1" />
                Draft with AI
              </>
            )}
          </Button>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={sending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={isEmail ? 'primary' : 'success'}
            // `sending`, not `busy`: an in-flight draft does not disarm a
            // send the sender has already written.
            disabled={sending || sent || !recipient || !ready}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending…
              </>
            ) : (
              <>
                <i className={`${icon} me-2`} />
                {isEmail ? 'Send Email' : 'Send WhatsApp'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default PassengerMessageModal;
